import type { DataPoint } from "regression";
import regression from "regression";
import { VISCOSITY_TEMPERATURE_TYPE } from "@/libs/calculations/constants";

// Work for this was originally from
// static/Imperial FluidProperties - with calcs2.xlsx

// Define the type for regression coefficients.
export type RegressionCoefficients = {
  type: string;
  c3: number;
  c2: number;
  c1: number;
  b: number;
};

// To change names make the changes here and for RegressionCoefficientsMap
export enum FluidPropertyField {
  controllingValue = "controllingValue",
  controlType = "controlType",
  bubblePointPressurePSIA = "bubblePointPressurePSIA",
  oilFormationVolumeFactorBBLSTB = "oilFormationVolumeFactorBBLSTB",
  solutionGasOilRatioSCFSTB = "solutionGasOilRatioSCFSTB",
  waterFormationVolumeFactorBBLSTB = "waterFormationVolumeFactorBBLSTB",
  solutionGasWaterRatioSCFSTB = "solutionGasWaterRatioSCFSTB",
  gasFormationVolumeFactorRCFSCF = "gasFormationVolumeFactorRCFSCF",
  liveOilViscosityCP = "liveOilViscosityCP",
  waterViscosityCP = "waterViscosityCP",
  gasViscosityCP = "gasViscosityCP",
  deadOilViscosityCP = "deadOilViscosityCP"
}

export type RegressionCoefficientsMap = {
  bubblePointPressurePSIA: RegressionCoefficients | null;
  oilFormationVolumeFactorBBLSTB: RegressionCoefficients | null;
  solutionGasOilRatioSCFSTB: RegressionCoefficients | null;
  waterFormationVolumeFactorBBLSTB: RegressionCoefficients | null;
  solutionGasWaterRatioSCFSTB: RegressionCoefficients | null;
  gasFormationVolumeFactorRCFSCF: RegressionCoefficients | null;
  liveOilViscosityCP: RegressionCoefficients | null;
  waterViscosityCP: RegressionCoefficients | null;
  gasViscosityCP: RegressionCoefficients | null;
  deadOilViscosityCP: RegressionCoefficients | null;
};

export interface CalibrationData {
  [FluidPropertyField.controllingValue]: number;
  [FluidPropertyField.controlType]: string;
  [FluidPropertyField.bubblePointPressurePSIA]: number;
  [FluidPropertyField.oilFormationVolumeFactorBBLSTB]: number;
  [FluidPropertyField.solutionGasOilRatioSCFSTB]: number | null;
  [FluidPropertyField.waterFormationVolumeFactorBBLSTB]: number | null;
  [FluidPropertyField.solutionGasWaterRatioSCFSTB]: number | null;
  [FluidPropertyField.gasFormationVolumeFactorRCFSCF]: number | null;
  [FluidPropertyField.liveOilViscosityCP]: number;
  [FluidPropertyField.waterViscosityCP]: number | null;
  [FluidPropertyField.gasViscosityCP]: number | null;
  [FluidPropertyField.deadOilViscosityCP]: number | null;
}

/**
 * Derive regression coefficients from an array of calibration data objects.
 *
 * Each object is expected to have the following properties:
 *   - controllingValue: the independent variable (e.g., temperature or pressure)
 *   - bubblePointPressurePSIA, oilFormationVolumeFactorBBLSTB, solutionGasOilRatioSCFSTB,
 *     waterFormationVolumeFactorBBLSTB, solutionGasWaterRatioSCFSTB, gasFormationVolumeFactorRCFSCF,
 *     liveOilViscosityCP, waterViscosityCP, gasViscosityCP: the dependent variables.
 *
 * For each dependent variable, we filter out any non–numeric (or null) values.
 * If there are at least four valid points, we compute the regression coefficients; otherwise, we return null.
 *
 * @param data - Array of calibration data objects.
 * @returns An object containing the regression coefficients for each property.
 */
export function deriveRegressionCoefficientsFromData(
  data: CalibrationData[]
): RegressionCoefficientsMap {
  // Use Partial<> until all properties are defined.
  const result: Partial<RegressionCoefficientsMap> = {};
  const properties: Array<
    Exclude<keyof RegressionCoefficientsMap, "controlType">
  > = [
    FluidPropertyField.bubblePointPressurePSIA,
    FluidPropertyField.oilFormationVolumeFactorBBLSTB,
    FluidPropertyField.solutionGasOilRatioSCFSTB,
    FluidPropertyField.waterFormationVolumeFactorBBLSTB,
    FluidPropertyField.solutionGasWaterRatioSCFSTB,
    FluidPropertyField.gasFormationVolumeFactorRCFSCF,
    FluidPropertyField.liveOilViscosityCP,
    FluidPropertyField.waterViscosityCP,
    FluidPropertyField.gasViscosityCP,
    FluidPropertyField.deadOilViscosityCP
  ];

  // We already know if data is all "pressure" or all "temperature" from earlier checks
  const dataSetType = data[0].controlType;

  properties.forEach(prop => {
    const xArr: number[] = [];
    const yArr: number[] = [];
    data.forEach(point => {
      const xVal = point.controllingValue;
      const yVal = point[prop] as number | null;
      if (
        typeof xVal === "number" &&
        !isNaN(xVal) &&
        typeof yVal === "number" &&
        !isNaN(yVal)
      ) {
        xArr.push(xVal);
        yArr.push(yVal);
      }
    });
    result[prop] = getBestFitCoefficients(xArr, yArr, dataSetType);
  });

  return result as RegressionCoefficientsMap;
}

/**
 * Compute a cubic polynomial regression if at least 4 data points exist;
 * otherwise return a simpler fallback or null.
 */
function getBestFitCoefficients(
  dataX: number[],
  dataY: number[],
  type: string
): RegressionCoefficients | null {
  const numPoints = dataX.length;
  const coeffResults: RegressionCoefficients = {
    type: type || "",
    c3: 0,
    c2: 0,
    c1: 0,
    b: 0
  };

  if (numPoints === 0) {
    return null;
  }
  // If fewer than 4 data points, fallback to b = first dependent value, c's = 0
  else if (numPoints < 4) {
    coeffResults.b = dataY[0];
    return coeffResults;
  }

  const formattedData = dataX.map((x, i) => [x, dataY[i]]) as DataPoint[];

  const result = regression.polynomial(formattedData, {
    order: 3,
    precision: 17
  });

  // Map regression results back to our RegressionCoefficients shape
  coeffResults.c3 = result.equation[0];
  coeffResults.c2 = result.equation[1];
  coeffResults.c1 = result.equation[2];
  coeffResults.b = result.equation[3];
  return coeffResults;
}

/**
 * Evaluate a polynomial at the given pressure or temperature
 * (depending on whether type === "pressure" or "temperature").
 */
export function evaluateLinearizationPolynomial(
  coeff: RegressionCoefficients | null,
  pressure: number,
  temperature: number
): number {
  // Decide the x-value based on controlType
  const x = coeff?.type === VISCOSITY_TEMPERATURE_TYPE ? temperature : pressure;

  return (
    (coeff?.c3 || 0) * Math.pow(x, 3) +
    (coeff?.c2 || 0) * Math.pow(x, 2) +
    (coeff?.c1 || 0) * x +
    (coeff?.b || 0)
  );
}
